import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { trackEvent } from '../services/analytics';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const PAGE = {
  url: 'https://stellarreply.com/free-airbnb-listing-audit',
  title: 'Free Airbnb Listing Audit for Hosts | StellarReply',
  metaDescription: 'Paste your Airbnb listing URL and get a free review-health score in seconds: your category ratings vs the 4.8 Superhost benchmark, what guests praise, and 3 quick wins. No signup required.',
  ogDescription: 'Get a free review-health score for your Airbnb listing: category ratings vs the Superhost benchmark, guest highlights, and 3 quick wins.',
  schemaName: 'Free Airbnb Listing Audit',
};

// SEO meta tags - set on mount (same pattern as FreeReplyGeneratorPage)
function useMetaTags() {
  useEffect(() => {
    document.title = PAGE.title;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(name.startsWith('og:') ? 'property' : 'name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', PAGE.metaDescription);
    setMeta('og:title', PAGE.title);
    setMeta('og:description', PAGE.ogDescription);
    setMeta('og:type', 'website');
    setMeta('og:url', PAGE.url);

    // Self-canonical — the SPA shell ships the homepage canonical, which tells
    // Google this page is a duplicate of /
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute('href') : null;
    if (canonical) canonical.setAttribute('href', PAGE.url);

    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.id = 'airbnb-audit-schema';
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": PAGE.schemaName,
      "url": PAGE.url,
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": PAGE.ogDescription,
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "GBP" }
    });
    document.head.appendChild(schema);
    return () => {
      if (canonical && prevCanonical) canonical.setAttribute('href', prevCanonical);
      document.getElementById('airbnb-audit-schema')?.remove();
    };
  }, []);
}

function ScoreDial({ score, label }) {
  const colour = score >= 70 ? '#16a34a' : score >= 55 ? '#f59e0b' : '#dc2626';
  return (
    <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
      <div style={{ fontSize: 56, fontWeight: 800, color: colour, lineHeight: 1 }}>{score}</div>
      <div style={{ fontSize: 13, color: '#6b7280', fontWeight: 600, marginTop: 4 }}>
        Listing health score - <span style={{ color: colour }}>{label}</span>
      </div>
    </div>
  );
}

function CategoryBar({ cat }) {
  // Airbnb category ratings live in the 4.0-5.0 band; map that to the bar width
  const pct = Math.max(4, Math.min(100, ((cat.rating - 4.0) / 1.0) * 100));
  const benchmarkPct = ((cat.benchmark - 4.0) / 1.0) * 100;
  const colour = cat.meets_benchmark ? '#16a34a' : '#f59e0b';
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
        <span style={{ fontWeight: 600, color: '#1a1a1a' }}>{cat.category}</span>
        <span style={{ color: colour, fontWeight: 700 }}>{cat.rating.toFixed(1)}</span>
      </div>
      <div style={{ position: 'relative', height: 8, background: '#f0ede8', borderRadius: 4 }}>
        <div style={{ position: 'absolute', left: 0, top: 0, height: 8, width: `${pct}%`, background: colour, borderRadius: 4, transition: 'width 0.6s ease' }} />
        <div title={`Superhost benchmark ${cat.benchmark}`} style={{ position: 'absolute', left: `${benchmarkPct}%`, top: -3, height: 14, width: 2, background: '#1a1a1a', borderRadius: 1 }} />
      </div>
    </div>
  );
}

export default function AirbnbAuditPage() {
  const navigate = useNavigate();
  const { shareId } = useParams();
  const isSharedView = !!shareId;

  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [honeypot, setHoneypot] = useState('');
  const [loadedAt] = useState(() => Date.now());

  // Email gate for the quick wins (shared localStorage key with the free generator)
  const [email, setEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [unlocked, setUnlocked] = useState(() => !!localStorage.getItem('sr_free_tool_email'));
  const [unlocking, setUnlocking] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);

  useMetaTags();

  const loadShared = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/free-tools/airbnb-audit/r/${shareId}`);
      if (!res.ok) throw new Error('This audit link has expired or does not exist.');
      setResult(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [shareId]);

  useEffect(() => {
    if (isSharedView) loadShared();
  }, [isSharedView, loadShared]);

  const handleAudit = async (e) => {
    e.preventDefault();
    if (!url.includes('/rooms/')) {
      setError("That doesn't look like a listing URL - paste a link like airbnb.co.uk/rooms/12345 (find it under Share on your listing).");
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const res = await fetch(`${API_URL}/free-tools/airbnb-audit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ airbnb_url: url.trim(), website: honeypot, loaded_at: loadedAt }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        // Without this a broken fetch is invisible: the Brotli bug failed every audit for two weeks unnoticed
        trackEvent('airbnb_audit_failed', { status: res.status });
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Audit failed. Please try again.');
      }
      const report = await res.json();
      setResult(report);
      trackEvent('airbnb_audit_completed', { score: report.score });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!email.includes('@') || !result) return;
    setUnlocking(true);
    try {
      await fetch(`${API_URL}/free-tools/airbnb-audit/${result.id}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), marketing_consent: marketingConsent }),
      });
      localStorage.setItem('sr_free_tool_email', email.trim());
      setUnlocked(true);
      trackEvent('airbnb_audit_lead_captured');
    } catch {
      // Don't block the user on a lead-capture failure
      setUnlocked(true);
    } finally {
      setUnlocking(false);
    }
  };

  const handleCopyShare = () => {
    navigator.clipboard.writeText(result.share_url).then(() => {
      setCopiedShare(true);
      setTimeout(() => setCopiedShare(false), 2000);
    });
  };

  const startTrial = () => navigate('/login?register=true&category=hospitality');

  const showAnalysis = unlocked || isSharedView;

  return (
    <div style={styles.page}>
      <style>{cssText}</style>

      {/* Nav */}
      <nav style={styles.nav}>
        <div style={styles.navInner}>
          <div style={styles.logo} onClick={() => navigate('/')}>
            Stellar<span style={{ color: '#16a34a' }}>Reply</span>
          </div>
          <button style={styles.navCta} onClick={startTrial}>
            Start free trial
          </button>
        </div>
      </nav>

      {/* Hero / Form */}
      <section style={styles.hero}>
        <div style={styles.heroInner}>
          <div style={styles.badge}>Free tool for Airbnb hosts - no signup required</div>
          <h1 style={styles.h1}>
            Free Airbnb<br />
            <span style={{ color: '#16a34a' }}>listing audit</span>
          </h1>
          <p style={styles.heroSub}>
            Paste your listing URL and get an instant review-health score: your category
            ratings against the 4.8 Superhost benchmark, what guests praise most, and
            three quick wins to lift your rating.
          </p>

          {!isSharedView && (
            <form onSubmit={handleAudit} style={styles.form}>
              <input
                style={styles.input}
                type="text"
                value={url}
                onChange={e => setUrl(e.target.value)}
                placeholder="https://www.airbnb.co.uk/rooms/12345678"
                maxLength={500}
                disabled={loading}
              />
              {/* Honeypot */}
              <input
                type="text" value={honeypot} onChange={e => setHoneypot(e.target.value)}
                autoComplete="off" tabIndex={-1}
                style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
                aria-hidden="true"
              />
              <button
                style={{
                  ...styles.btn, marginTop: 12,
                  opacity: loading || !url.trim() ? 0.45 : 1,
                  cursor: loading || !url.trim() ? 'not-allowed' : 'pointer',
                }}
                type="submit" disabled={loading || !url.trim()}
              >
                {loading ? 'Reading your reviews...' : 'Audit my listing'}
              </button>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8, textAlign: 'center' }}>
                Works with any public Airbnb listing. Find your link under Share on the listing page.
              </div>
            </form>
          )}

          {isSharedView && loading && (
            <div style={{ color: '#6b7280', fontSize: 14, marginTop: 24 }}>Loading audit...</div>
          )}

          {error && <div style={styles.errorBox} className="audit-fade">{error}</div>}

          {/* Report */}
          {result && (
            <div className="audit-fade" style={{ marginTop: 28, textAlign: 'left' }}>
              {/* Listing header + score */}
              <div style={styles.reportCard}>
                <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 700, marginBottom: 6 }}>
                  Listing audit by StellarReply
                </div>
                <div style={{ fontSize: 17, fontWeight: 800, color: '#1a1a1a' }}>{result.listing.title}</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                  {[result.listing.location, `${result.listing.rating} ★`, `${result.listing.review_count} reviews`].filter(Boolean).join(' · ')}
                </div>
                <ScoreDial score={result.score} label={result.score_label} />
                {showAnalysis && result.analysis?.headline && (
                  <p style={{ fontSize: 14, color: '#1a1a1a', lineHeight: 1.6, textAlign: 'center', margin: '8px 0 0' }}>
                    {result.analysis.headline}
                  </p>
                )}
              </div>

              {/* Category ratings vs benchmark */}
              {result.category_ratings?.length > 0 && (
                <div style={styles.reportCard}>
                  <h2 style={styles.cardTitle}>Category ratings vs the Superhost benchmark</h2>
                  <p style={{ ...styles.seoText, marginBottom: 14 }}>
                    The black marker is {result.superhost_benchmark} - the rating Superhosts hold. Amber bars are where your next 5-star reviews come from.
                  </p>
                  {result.category_ratings.map(cat => <CategoryBar key={cat.category} cat={cat} />)}
                </div>
              )}

              {/* Guest tags */}
              {result.review_tags?.length > 0 && (
                <div style={styles.reportCard}>
                  <h2 style={styles.cardTitle}>What guests mention most</h2>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {result.review_tags.map(tag => (
                      <span key={tag.name} style={styles.tagChip}>
                        {tag.name} <span style={{ color: '#16a34a', fontWeight: 700 }}>{tag.count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* AI analysis — email-gated on the direct flow, open on shared links */}
              <div style={styles.reportCard}>
                <h2 style={styles.cardTitle}>Your 3 quick wins</h2>
                {showAnalysis ? (
                  <>
                    {result.analysis?.strengths?.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        {result.analysis.strengths.map(s => (
                          <div key={s.theme} style={{ marginBottom: 10 }}>
                            <span style={{ fontSize: 13, fontWeight: 700, color: '#16a34a' }}>&#10003; {s.theme}</span>
                            <p style={{ ...styles.seoText, margin: '2px 0 0' }}>{s.detail}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {result.analysis?.quick_wins?.map((win, i) => (
                      <div key={win.title} style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                        <div style={styles.winNumber}>{i + 1}</div>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>{win.title}</div>
                          <p style={{ ...styles.seoText, margin: '2px 0 0' }}>{win.detail}</p>
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <form onSubmit={handleUnlock}>
                    <p style={{ ...styles.seoText, marginBottom: 12 }}>
                      Enter your email to unlock your strengths and the three specific actions
                      our AI found for this listing.
                    </p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <input
                        style={{ ...styles.input, flex: '1 1 200px', padding: '10px 14px', fontSize: 14 }}
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        required
                      />
                      <button
                        type="submit"
                        style={{ ...styles.btn, width: 'auto', padding: '10px 20px', fontSize: 14, opacity: unlocking ? 0.7 : 1 }}
                        disabled={unlocking}
                      >
                        {unlocking ? 'Unlocking...' : 'Unlock quick wins'}
                      </button>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10, fontSize: 12, color: '#6b7280', cursor: 'pointer', lineHeight: 1.5 }}>
                      <input
                        type="checkbox"
                        checked={marketingConsent}
                        onChange={e => setMarketingConsent(e.target.checked)}
                        style={{ marginTop: 2, accentColor: '#16a34a' }}
                      />
                      <span>Email me occasional hosting tips and StellarReply offers. Unsubscribe anytime.</span>
                    </label>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>
                      Your email is only used as described in our{' '}
                      <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#16a34a' }}>privacy policy</a>.
                      The checkbox is optional - unlocking works either way.
                    </div>
                  </form>
                )}
              </div>

              {/* Share + CTA */}
              {showAnalysis && (
                <>
                  {!isSharedView && (
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
                      <button style={{ ...styles.btn, width: 'auto', padding: '10px 24px', fontSize: 14 }} onClick={handleCopyShare}>
                        {copiedShare ? 'Link copied!' : 'Copy share link'}
                      </button>
                      <button style={styles.btnGhost} onClick={() => { setResult(null); setUrl(''); }}>
                        Audit another listing
                      </button>
                    </div>
                  )}

                  <div style={{ ...styles.ctaCard, textAlign: 'center' }}>
                    <h2 style={{ fontSize: 19, fontWeight: 800, margin: '0 0 8px', color: '#1a1a1a' }}>
                      {result.analysis?.teaser || 'Your individual reviews hold far more than this.'}
                    </h2>
                    <p style={{ fontSize: 14, color: '#6b7280', margin: '0 0 16px', lineHeight: 1.6 }}>
                      StellarReply's Listing Intelligence reads your actual guest reviews and writes
                      an optimised description in your guests' own words, plus title suggestions,
                      amenity actions and a pricing signal. And it drafts a warm host reply to every
                      review. Flat &#163;20/month, 14-day free trial.
                    </p>
                    <button style={{ ...styles.btn, width: 'auto', padding: '12px 28px' }} onClick={startTrial}>
                      {isSharedView ? 'Audit your own listing free' : 'Start 14-day free trial'}
                    </button>
                    {isSharedView && (
                      <p style={{ fontSize: 12, marginTop: 12 }}>
                        <span onClick={() => navigate('/free-airbnb-listing-audit')} style={{ color: '#16a34a', cursor: 'pointer', textDecoration: 'underline' }}>
                          Run a free audit on your listing
                        </span>
                      </p>
                    )}
                    {!isSharedView && (
                      <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 10 }}>
                        No credit card required. Cancel anytime.
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {!result && !isSharedView && (
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 20, textAlign: 'center' }}>
              100% free. Also try our{' '}
              <span onClick={() => navigate('/free-airbnb-review-response-generator')} style={{ color: '#16a34a', cursor: 'pointer', textDecoration: 'underline' }}>
                free Airbnb review response generator
              </span>.
            </p>
          )}
        </div>
      </section>

      {/* SEO copy */}
      <section style={{ padding: '20px 20px 60px' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>What does the audit check?</h2>
            <p style={styles.seoText}>
              We read your listing's public review data: the overall rating, per-category
              ratings (cleanliness, accuracy, check-in, communication, location and value)
              and what guests mention most often. Each category is compared against the 4.8
              Superhost benchmark, because a single weak category is usually what separates
              a 4.7 listing from a 4.9 one.
            </p>
          </div>
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Why your category ratings matter</h2>
            <p style={styles.seoText}>
              Airbnb's search algorithm favours listings with high, consistent ratings, and
              guests filter by them. Most hosts only watch the overall star rating, but the
              category breakdown tells you exactly where the next lost half-star will come
              from - and it's usually fixable in a weekend. Our free audit turns that
              breakdown into three specific actions.
            </p>
          </div>
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>More free tools for hosts</h2>
            <p style={styles.seoText}>
              Need to reply to a guest review right now? Use the{' '}
              <a href="/free-airbnb-review-response-generator" style={{ color: '#16a34a', fontWeight: 600 }}>
                free Airbnb review response generator
              </a>. Or read our guides on{' '}
              <a href="/blog/optimise-airbnb-listing-using-guest-reviews.html" style={{ color: '#16a34a', fontWeight: 600 }}>
                optimising your listing using guest reviews
              </a>{' '}and{' '}
              <a href="/blog/airbnb-review-response-templates.html" style={{ color: '#16a34a', fontWeight: 600 }}>Airbnb Review Response Templates for Hosts</a>. Or see everything{' '}
              <a href="/for-airbnb-hosts" style={{ color: '#16a34a', fontWeight: 600 }}>
                StellarReply does for Airbnb hosts
              </a>.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <span>Powered by </span>
        <a href="https://stellarreply.com" style={{ color: '#16a34a', textDecoration: 'none', fontWeight: 600 }}>
          StellarReply
        </a>
      </footer>
    </div>
  );
}

const cssText = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&display=swap');
  @keyframes auditFadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
  .audit-fade { animation: auditFadeIn 0.5s ease both; }
`;

const styles = {
  page: { fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif", minHeight: '100vh', background: '#faf9f6' },
  nav: { borderBottom: '1px solid #e8e5df', background: '#fff' },
  navInner: { maxWidth: 900, margin: '0 auto', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { fontSize: 18, fontWeight: 800, color: '#1a1a1a', cursor: 'pointer' },
  navCta: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans', system-ui, sans-serif" },
  hero: { padding: '60px 20px 20px', textAlign: 'center' },
  heroInner: { maxWidth: 620, margin: '0 auto' },
  badge: { display: 'inline-block', fontSize: 12, fontWeight: 600, color: '#16a34a', background: 'rgba(22,163,74,0.08)', padding: '4px 14px', borderRadius: 20, marginBottom: 16 },
  h1: { fontSize: 36, fontWeight: 800, color: '#1a1a1a', lineHeight: 1.2, margin: '0 0 16px' },
  heroSub: { fontSize: 15, color: '#6b7280', lineHeight: 1.6, margin: '0 0 32px', maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' },
  form: { maxWidth: 520, margin: '0 auto', textAlign: 'left' },
  input: { width: '100%', padding: '14px 18px', borderRadius: 10, border: '1px solid #d4d1cc', fontSize: 15, fontFamily: "'DM Sans', system-ui, sans-serif", outline: 'none', boxSizing: 'border-box', background: '#fff' },
  btn: { width: '100%', padding: '14px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #16a34a, #059669)', color: '#fff', fontSize: 16, fontWeight: 800, cursor: 'pointer', fontFamily: "'DM Sans', system-ui, sans-serif" },
  btnGhost: { padding: '10px 24px', borderRadius: 10, border: '1px solid #d4d1cc', background: '#fff', color: '#1a1a1a', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', system-ui, sans-serif" },
  errorBox: { marginTop: 16, padding: '12px 16px', background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.15)', borderRadius: 8, color: '#dc2626', fontSize: 13, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' },
  reportCard: { background: '#fff', borderRadius: 12, border: '1px solid #e8e5df', padding: '20px 24px', marginBottom: 16 },
  card: { background: '#fff', borderRadius: 12, border: '1px solid #e8e5df', padding: '20px 24px', marginBottom: 16 },
  cardTitle: { fontSize: 15, fontWeight: 700, margin: '0 0 12px', color: '#1a1a1a' },
  seoText: { fontSize: 14, color: '#6b7280', lineHeight: 1.7, margin: 0 },
  tagChip: { fontSize: 13, color: '#1a1a1a', background: '#f5f3ef', border: '1px solid #e8e5df', borderRadius: 16, padding: '5px 12px' },
  winNumber: { minWidth: 26, height: 26, borderRadius: '50%', background: 'rgba(22,163,74,0.1)', color: '#16a34a', fontWeight: 800, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 1 },
  ctaCard: { background: '#fff', borderRadius: 12, border: '2px solid #16a34a', padding: '28px', marginTop: 8 },
  footer: { textAlign: 'center', padding: '20px', fontSize: 12, color: '#9ca3af', borderTop: '1px solid #e8e5df' },
};
